import { Observable, combineLatest, forkJoin, from, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from 'src/app/auth-o3/shared/services/api.service';
import { AuthService } from 'src/app/auth-o3/shared/services/auth.service';
import { environment } from 'src/environments/environment';
import { DOCGEN_ENDPOINTS } from '../model/consts/docgen-endpoints.const';
import { DOCUMENT_TYPE } from '../model/consts/document-type.const';
import { DOCUMENT_STATUS } from '../model/enums/document-status.enum';
import { LANGUAGE } from '../model/enums/language.enum';
import { DocumentFile } from '../model/interfaces/document-file.interface';
import { DraftableResponse } from '../model/interfaces/draftable-response.interface';
import {
  DocDetails,
  MIME_TYPE_BY_FILE_EXTENSION,
} from '../product-capture-details/overview/documents/documents.model';

const HOSTNAME_DOCGEN_ENDPOINT = environment.api.docgen;
const HOSTNAME_DOCCOMPARE_ENDPOINT = environment.api.docComparison;

@Injectable({
  providedIn: 'root',
})
export class DocService {
  private user;

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private http: HttpClient
  ) {
    this.authService.onUserSession.pipe(take(1)).subscribe((user) => {
      this.user = user;
    });
  }

  approve(docId: number): Observable<void> {
    const payload = {
      status: DOCUMENT_STATUS.AVAILABLE,
      approvedBy: this.user.accessToken.payload.username,
      approvedById: this.user.accessToken.payload.client_id,
    };
    return this.apiService.put(docId + '', payload, HOSTNAME_DOCGEN_ENDPOINT);
  }

  delete(docId: number): Observable<void> {
    const payload = {};
    return this.apiService.delete(
      docId + '',
      payload,
      HOSTNAME_DOCGEN_ENDPOINT
    );
  }

  fetchDocs(type: string, productId: number): Observable<DocumentFile[]> {
    const params = new HttpParams({
      fromObject: { type, productId: productId.toString() },
    });
    const querys = params.toString();
    return this.apiService.get('?' + querys, HOSTNAME_DOCGEN_ENDPOINT);
  }

  fetchAllDocs(productId: number): Observable<DocumentFile[]> {
    return forkJoin([
      this.fetchDocs(DOCUMENT_TYPE[0].id, productId),
      this.fetchDocs(DOCUMENT_TYPE[1].id, productId),
      this.fetchDocs(DOCUMENT_TYPE[2].id, productId),
      this.fetchDocs(DOCUMENT_TYPE[3].id, productId),
    ]).pipe(
      map(([termSheet, finalTerms, kid, other]) => [
        ...termSheet,
        ...finalTerms,
        ...kid,
        ...other,
      ])
    );
  }

  download(docId: number): void {
    this.apiService
      .get<{ url: string }>(
        DOCGEN_ENDPOINTS.READ_PDF + docId,
        HOSTNAME_DOCGEN_ENDPOINT
      )
      .subscribe(({ url }) => {
        window.open(url);
      });
  }

  downloadDontOpen(docId: number): Observable<any> {
    return this.apiService.get<{ url: string }>(
      DOCGEN_ENDPOINTS.READ_PDF + docId,
      HOSTNAME_DOCGEN_ENDPOINT
    );
  }

  downloadAll(productId: number): void {
    this.fetchAllDocs(productId)
      .pipe(
        tap((docs) => {
          docs.forEach((doc) => {
            this.download(doc.id);
          });
        })
      )
      .subscribe();
  }

  reject(docId: number): Observable<void> {
    const payload = {
      status: DOCUMENT_STATUS.NOT_AVAILABLE,
      approvedBy: this.user.accessToken.payload.username,
      approvedById: this.user.accessToken.payload.client_id,
    };
    return this.apiService.put(docId + '', payload, HOSTNAME_DOCGEN_ENDPOINT);
  }

  request(
    productId: number,
    language: string,
    documentState: string,
    type: string,
    docFormat: string,
    subtype?: string
  ): Observable<void> {
    let base64DocFileType = docFormat;
    const payload = {
      productId: Number(productId),
      type,
      createdBy: this.user.accessToken.payload.username,
      createdById: this.user.accessToken.payload.client_id,
      language,
      documentState,
      base64DocFileType,
      ...(subtype && { subtype }),
    };
    return this.apiService.post('', payload, HOSTNAME_DOCGEN_ENDPOINT);
  }

  upload(
    type: string,
    language: LANGUAGE,
    documentState: string,
    productId: number,
    doc: File,
    base64Doc: string,
    isin: string
  ): Observable<number> {
    const fileType = doc.name.split('.');
    const payload = {
      type,
      productId: Number(productId),
      createdBy: this.user.accessToken.payload.username,
      createdById: this.user.accessToken.payload.client_id,
      language,
      documentState,
      base64Doc: base64Doc.replace('data:application/pdf;base64,', ''),
      base64DocFileType: fileType[fileType.length - 1],
      fileName: `${isin ? `${isin} - ` : ''}${doc.name}`,
    };

    return this.apiService.post('', payload, HOSTNAME_DOCGEN_ENDPOINT);
  }

  getFileName(fileName: string): string {
    const paths = fileName?.split('/') || [fileName];
    return paths[paths.length - 1];
  }

  file2Base64(file: File | Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result?.toString() || '');
      reader.onerror = (error) => reject(error);
    });
  }

  async pdfLinkToBlobToBase64(url: string): Promise<string> {
    let blob = await fetch(url).then((res) => res.blob());
    let base64String = await this.file2Base64(blob).then((output) => output);
    return base64String.split(',')[1];
  }

  async pdfIdToBase64(id: number): Promise<string> {
    return this.downloadDontOpen(id)
      .toPromise()
      .then(({ url }) => this.pdfLinkToBlobToBase64(url))
      .then((b64String) => b64String);
  }

  draftableGetArguments(
    docIdOne: number,
    docIdTwo: number
  ): Observable<string[]> {
    const first$ = this.getBase64(docIdOne);
    const second$ = this.getBase64(docIdTwo);
    return combineLatest([first$, second$]).pipe(take(1));
  }

  getPDFAsBase64(id: number): Observable<string> {
    return this.http.get(
      `${HOSTNAME_DOCGEN_ENDPOINT}${DOCGEN_ENDPOINTS.READ_BASE64_PDF + id}`,
      {
        ...this.apiService.getOptions(),
        responseType: 'text',
      }
    );
  }

  getBase64(id: number): Observable<string> {
    return this.http.get(
      `${HOSTNAME_DOCGEN_ENDPOINT}${DOCGEN_ENDPOINTS.READ_BASE64 + id}`,
      {
        ...this.apiService.getOptions(),
        responseType: 'text',
      }
    );
  }

  draftableGetLink(
    expected: DocDetails,
    actual: DocDetails,
    draftable: boolean = true
  ): Observable<DraftableResponse> {
    const mimeType = `application/${
      MIME_TYPE_BY_FILE_EXTENSION[expected.type]
    }`;

    const payload = {
      mimeType,
      expected: expected.base64,
      actual: actual.base64,
      draftable,
    };
    return this.http.post<any>(
      HOSTNAME_DOCCOMPARE_ENDPOINT,
      payload,
      this.apiService.getOptions()
    );
  }

  downloadGovernanceChecksDocument(
    productId: number,
    governanceReportId: number
  ): Observable<{ document: string }> {
    const payload = {
      productId: Number(productId),
      governanceReportId,
      base64DocFileType: 'pdf',
    };
    return this.apiService.post(
      'raw/GOVERNANCE_REPORT',
      payload,
      HOSTNAME_DOCGEN_ENDPOINT
    );
  }

  base64ToFile(base64: string, filename: string): Observable<File> {
    return from(fetch(`data:application/pdf;base64,${base64}`)).pipe(
      switchMap((res) => res.blob()),
      switchMap((blob) => {
        const file = new File([blob], filename, {
          type: 'application/pdf',
        });
        return of(file);
      })
    );
  }
}
